import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { 
  MdClose, 
  MdDelete, 
  MdSearch, 
  MdRefresh,
  MdShoppingCart
} from 'react-icons/md';
import { CartItem } from '../App';

const ViewAllButton = styled.button`
  background: transparent;
  color: var(--color-champagne);
  padding: 0.75rem 2.5rem;
  border-radius: 30px;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  border: 1px solid var(--color-champagne);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.05);
  
  &:hover {
    background: var(--color-champagne);
    color: var(--color-obsidian);
    box-shadow: 0 0 20px rgba(212, 175, 55, 0.3);
    transform: translateY(-2px);
  }
`;

const BrandCollabWrapper = styled.div`
  width: 100%;
  margin-top: 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    margin-top: 3.5rem;
  }
`;

const BrandCollabHeading = styled.h3`
  font-family: 'Cinzel', serif;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: var(--color-champagne);
  text-align: center;
  margin-bottom: 2.5rem;
  text-transform: uppercase;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 40px;
    height: 1px;
    background: var(--color-champagne);
    margin: 0.75rem auto 0;
    opacity: 0.5;
  }
`;

const BrandPartnersBar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4rem;
  flex-wrap: wrap;
  padding: 3rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  width: 100%;
  
  @media (max-width: 1024px) {
    gap: 3rem;
    padding: 2.5rem 0;
  }
  
  @media (max-width: 768px) {
    gap: 2rem;
    padding: 2rem 0;
  }
`;

const BrandPartnerLogo = styled.img`
  height: 80px;
  object-fit: contain;
  opacity: 0.35;
  filter: grayscale(100%) brightness(200%);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    opacity: 0.95;
    filter: grayscale(0%) brightness(100%);
    transform: scale(1.06);
  }
  
  @media (max-width: 1024px) {
    height: 65px;
  }
  
  @media (max-width: 768px) {
    height: 48px;
  }
`;

const FeaturedHeader = styled.div`
  text-align: center;
  margin-bottom: 3.5rem;
  width: 100%;
`;

const FeaturedSub = styled.span`
  color: var(--color-champagne);
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  display: block;
  margin-bottom: 0.75rem;
`;

const FeaturedTitle = styled.h2`
  font-family: 'Cinzel', serif;
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--color-pearl);
  
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 1px;
    background: var(--color-champagne);
    margin: 1.25rem auto 0;
    opacity: 0.6;
  }
`;

// Layout Container
const CatalogLayout = styled.section<{ isFeaturedOnly: boolean }>`
  max-width: 1600px;
  margin: 0 auto;
  padding: ${props => props.isFeaturedOnly ? '1.5rem 2rem 4rem' : '8.5rem 2rem 4rem'};
  display: flex;
  gap: 2.5rem;
  position: relative;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    padding: ${props => props.isFeaturedOnly ? '1.25rem 1.5rem 3rem' : '7.5rem 1.5rem 3rem'};
    gap: 1.5rem;
  }
  @media (max-width: 768px) {
    padding: ${props => props.isFeaturedOnly ? '1rem 1rem 2rem' : '6.5rem 1rem 2rem'};
  }
`;

// Sidebar Filters
const FilterSidebar = styled.aside`
  width: 300px;
  flex-shrink: 0;
  background: rgba(22, 22, 22, 0.6);
  border: 1px solid rgba(212, 175, 55, 0.15);
  border-radius: 16px;
  padding: 2rem 1.5rem;
  backdrop-filter: blur(20px);
  height: fit-content;
  position: sticky;
  top: 90px;
  z-index: 10;
  
  @media (max-width: 1024px) {
    width: 100%;
    position: relative;
    top: 0;
    padding: 1.5rem;
  }
`;

const FilterSectionTitle = styled.h3`
  font-size: 0.95rem;
  color: var(--color-champagne);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(212, 175, 55, 0.2);
  padding-bottom: 0.75rem;
`;

const FilterGroup = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled.span`
  display: block;
  font-size: 0.85rem;
  color: var(--color-slate-300);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.75rem;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: rgba(11, 11, 11, 0.5);
  border: 1px solid rgba(212, 175, 55, 0.15);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  
  &:focus-within {
    border-color: var(--color-champagne);
  }
`;

const SearchInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: var(--color-pearl);
  font-size: 0.85rem;
  width: 100%;
  margin-left: 0.5rem;
  
  &::placeholder {
    color: var(--color-slate-500);
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--color-slate-300);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  user-select: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: var(--color-champagne);
  }
  
  input {
    appearance: none;
    background: rgba(11, 11, 11, 0.8);
    border: 1px solid rgba(212, 175, 55, 0.3);
    width: 16px;
    height: 16px;
    border-radius: 4px;
    cursor: pointer;
    display: grid;
    place-content: center;
    position: relative;
    transition: all 0.3s ease;
    
    &:checked {
      background: var(--color-champagne);
      border-color: var(--color-champagne);
      
      &::before {
        content: "";
        width: 8px;
        height: 8px;
        background-color: var(--color-obsidian);
        border-radius: 2px;
      }
    }
  }
`;

const ClearButton = styled.button`
  width: 100%;
  background: rgba(212, 175, 55, 0.05);
  color: var(--color-champagne);
  border: 1px solid rgba(212, 175, 55, 0.3);
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  
  &:hover {
    background: rgba(212, 175, 55, 0.15);
    border-color: var(--color-champagne);
  }
`;

const SectionDivider = styled.div`
  margin: 6rem 0;
  position: relative;
  height: 2px;
  width: 100%;
`;

// Main Content (Grid + Header)
const CatalogContent = styled.div`
  flex-grow: 1;
`;

const CatalogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  background: rgba(22, 22, 22, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  backdrop-filter: blur(12px);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
`;

const ResultsCount = styled.span`
  color: var(--color-slate-400);
  font-size: 0.9rem;
  
  strong {
    color: var(--color-pearl);
    font-weight: 600;
  }
`;

const SortDropdown = styled.select`
  background: rgba(11, 11, 11, 0.8);
  border: 1px solid rgba(212, 175, 55, 0.2);
  color: var(--color-slate-300);
  padding: 0.5rem 1.5rem 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.85rem;
  outline: none;
  cursor: pointer;
  
  &:focus {
    border-color: var(--color-champagne);
  }
`;

// Product Cards
const ProductGrid = styled.div<{ isFeaturedOnly?: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.isFeaturedOnly ? 'repeat(4, 1fr)' : 'repeat(auto-fill, minmax(280px, 1fr))'};
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: ${props => props.isFeaturedOnly ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(260px, 1fr))'};
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled.div`
  background: linear-gradient(135deg, rgba(26, 26, 26, 0.8) 0%, rgba(18, 18, 18, 0.9) 100%);
  border: 1px solid rgba(212, 175, 55, 0.12);
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.6),
      0 0 30px rgba(212, 175, 55, 0.15);
    border-color: rgba(212, 175, 55, 0.35);
  }
`;

const Badge = styled.span<{ availability: string }>`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${props => 
    props.availability === 'Pre-Order' ? '#059669' :
    props.availability === 'Sold Out' ? '#4b5563' :
    'linear-gradient(135deg, var(--color-champagne) 0%, var(--color-rose-gold) 100%)'
  };
  color: ${props => props.availability === 'Pre-Order' || props.availability === 'Sold Out' ? 'white' : 'var(--color-obsidian)'};
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  z-index: 5;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
`;

const CardImageWrapper = styled.div`
  height: 280px;
  background: rgba(11, 11, 11, 0.6);
  position: relative;
  overflow: hidden;
  cursor: pointer;
`;

const CardImage = styled.img<{ isHovered: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.8s ease, opacity 0.5s ease;
  transform: ${props => props.isHovered ? 'scale(1.05)' : 'scale(1)'};
`;

const CardImageAlt = styled.img<{ isVisible: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.5s ease, transform 0.8s ease;
  transform: ${props => props.isVisible ? 'scale(1.05)' : 'scale(1)'};
`;

const HoverOverlay = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.4s ease;
  z-index: 4;
`;

const QuickViewBtn = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  backdrop-filter: blur(8px);
  
  &:hover {
    background: white;
    color: black;
  }
`;

const CardInfo = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const BrandLabel = styled.span`
  font-size: 0.65rem;
  color: var(--color-champagne);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 800;
  margin-bottom: 0.5rem;
`;

const ProductTitle = styled.h4`
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--color-pearl);
  margin-bottom: 0.35rem;
  line-height: 1.25;
`;

const ProductSubtitle = styled.h5`
  font-size: 0.85rem;
  color: var(--color-slate-400);
  font-weight: 550;
  margin-bottom: 1rem;
`;

const CardBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding-top: 1rem;
`;

const PriceTag = styled.span`
  font-size: 1.2rem;
  font-weight: 900;
  color: var(--color-pearl);
`;

const AddCartIconBtn = styled.button`
  background: var(--color-champagne);
  color: var(--color-obsidian);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  box-shadow: 0 4px 10px rgba(212, 175, 55, 0.25);
  
  &:hover:not(:disabled) {
    background: var(--color-rose-gold);
    transform: scale(1.05);
  }
  
  &:disabled {
    background: #4b5563;
    color: #9ca3af;
    box-shadow: none;
    cursor: not-allowed;
  }
`;

// Quick View Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(10px);
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #161616 0%, #0b0b0b 100%);
  border: 1px solid rgba(212, 175, 55, 0.25);
  border-radius: 24px;
  max-width: 1000px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.8);
  
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`;

const ModalCloseBtn = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(212, 175, 55, 0.2);
  color: var(--color-slate-300);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  z-index: 10;
  
  &:hover {
    color: var(--color-champagne);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ModalGallery = styled.div`
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  
  @media (max-width: 850px) {
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    padding: 1.5rem;
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: 12px;
  border: 1px solid rgba(212, 175, 55, 0.15);
  
  @media (max-width: 480px) {
    height: 250px;
  }
`;

const ThumbnailContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-champagne);
    border-radius: 2px;
  }
`;

const Thumbnail = styled.img<{ isActive: boolean }>`
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid ${props => props.isActive ? 'var(--color-champagne)' : 'rgba(255, 255, 255, 0.1)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--color-champagne);
  }
`;

const ModalDetails = styled.div`
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: 850px) {
    padding: 1.5rem;
  }
`;

const ModalTitle = styled.h2`
  font-size: 2rem;
  font-weight: 900;
  color: var(--color-pearl);
  margin-bottom: 0.5rem;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const ModalPrice = styled.div`
  font-size: 1.8rem;
  font-weight: 900;
  color: var(--color-champagne);
  margin-bottom: 1.5rem;
`;

const ModalDesc = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: var(--color-slate-300);
  margin-bottom: 2rem;
`;

const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 2rem;
  background: rgba(255, 255, 255, 0.02);
  padding: 1.25rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const SpecItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  
  span.label {
    font-size: 0.7rem;
    text-transform: uppercase;
    color: var(--color-slate-500);
    font-weight: 800;
    letter-spacing: 0.05em;
  }
  span.value {
    font-size: 0.85rem;
    color: var(--color-slate-200);
    font-weight: 600;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const AddToCartBtn = styled.button`
  flex: 1;
  background: var(--color-champagne);
  color: var(--color-obsidian);
  border: 1px solid var(--color-champagne);
  padding: 1rem;
  border-radius: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.9rem;
  box-shadow: 0 8px 20px rgba(212, 175, 55, 0.25);
  
  &:hover:not(:disabled) {
    background: var(--color-obsidian);
    color: var(--color-champagne);
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #4b5563;
    color: #9ca3af;
    border-color: #4b5563;
    box-shadow: none;
    cursor: not-allowed;
  }
`;

// Cart Drawer
const CartDrawerOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  z-index: 2500;
  opacity: ${props => props.isOpen ? 1 : 0};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  transition: opacity 0.5s ease;
`;

const CartDrawer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 450px;
  max-width: 100%;
  background: linear-gradient(135deg, #111111 0%, #070707 100%);
  border-left: 1px solid rgba(212, 175, 55, 0.25);
  z-index: 2600;
  display: flex;
  flex-direction: column;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: -20px 0 50px rgba(0, 0, 0, 0.8);
`;

const CartHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    font-size: 1.25rem;
    font-weight: 800;
    color: var(--color-pearl);
    letter-spacing: 0.02em;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    span {
      color: var(--color-champagne);
    }
  }
`;

const CloseDrawerBtn = styled.button`
  background: transparent;
  color: var(--color-slate-400);
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  
  &:hover {
    color: var(--color-champagne);
    background: rgba(255, 255, 255, 0.05);
  }
`;

const CartItemsContainer = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-champagne);
    border-radius: 2px;
  }
`;

const EmptyCartState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--color-slate-400);
  text-align: center;
  gap: 1rem;
  
  .cart-icon {
    font-size: 3.5rem;
    color: var(--color-slate-600);
  }
`;

const CartItemRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1rem;
  position: relative;
`;

const CartItemThumb = styled.img`
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid rgba(212, 175, 55, 0.15);
`;

const CartItemDetails = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  
  h4 {
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--color-pearl);
    margin: 0;
  }
  p.scale {
    font-size: 0.75rem;
    color: var(--color-slate-500);
    margin: 0;
    text-transform: uppercase;
    font-weight: 600;
  }
  span.price {
    font-size: 0.95rem;
    font-weight: 800;
    color: var(--color-champagne);
  }
`;

const QtyControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(11, 11, 11, 0.8);
  border: 1px solid rgba(212, 175, 55, 0.25);
  padding: 0.25rem;
  border-radius: 6px;
  
  button {
    background: transparent;
    color: var(--color-slate-300);
    border: none;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.8rem;
    
    &:hover {
      color: var(--color-champagne);
    }
  }
  
  span {
    color: var(--color-pearl);
    font-size: 0.85rem;
    min-width: 20px;
    text-align: center;
    font-weight: 700;
  }
`;

const DeleteBtn = styled.button`
  background: transparent;
  color: var(--color-slate-500);
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  
  &:hover {
    color: #ef4444;
  }
`;

const CartFooter = styled.div`
  padding: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(11, 11, 11, 0.4);
`;

const CartSummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  
  span.label {
    color: var(--color-slate-400);
    font-size: 0.95rem;
    font-weight: 600;
  }
  span.total {
    color: var(--color-champagne);
    font-size: 1.4rem;
    font-weight: 900;
  }
`;

const CheckoutBtn = styled.button`
  width: 100%;
  background: var(--color-champagne);
  color: var(--color-obsidian);
  border: 1px solid var(--color-champagne);
  padding: 1.15rem;
  border-radius: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.95rem;
  box-shadow: 0 10px 25px rgba(212, 175, 55, 0.3);
  
  &:hover {
    background: var(--color-obsidian);
    color: var(--color-champagne);
    transform: translateY(-2px);
  }
`;

// Products Data List
const products = [
  {
    id: 1,
    name: "Loki",
    subtitle: "God of Mischief",
    description: "The charismatic God of Mischief in his signature gold horned helmet and ornate armor, wielding his scepter.",
    price: 675,
    scale: "1:4 Scale",
    dimensions: "23.5\" H x 12.0\" W x 11.5\" D",
    weight: "18 lbs",
    material: "Polystone & Fabric",
    category: "Avengers",
    availability: "Available Now",
    images: [
      "/assets/PRODUCTS/Loki/images/1.png",
      "/assets/PRODUCTS/Loki/images/2.png",
      "/assets/PRODUCTS/Loki/images/3.png",
      "/work-images/loki-1.jpg",
      "/work-images/loki-2.jpg"
    ]
  },
  {
    id: 2,
    name: "Miles Morales & Venom Diorama",
    subtitle: "Ultimate Showdown",
    description: "A highly dynamic battle diorama showcasing Miles Morales leaping away as a massive Venom lunges with exposed claws.",
    price: 890,
    scale: "1:4 Scale",
    dimensions: "28.0\" H x 18.0\" W x 16.5\" D",
    weight: "25 lbs",
    material: "Polystone",
    category: "Spider-Man",
    availability: "Available Now",
    images: [
      "/assets/PRODUCTS/VENOM SPIDERMAN BB/images/1.png",
      "/assets/PRODUCTS/VENOM SPIDERMAN BB/images/2.png",
      "/assets/PRODUCTS/VENOM SPIDERMAN BB/images/3.png",
      "/work-images/IMG_1681.JPEG.jpg"
    ]
  },
  {
    id: 3,
    name: "Rocket Raccoon",
    subtitle: "Guardians of the Galaxy",
    description: "The hot-tempered heavy-weapons expert featuring detailed fur sculpting, a combat suit, and custom blaster rifle.",
    price: 280,
    scale: "1:6 Scale",
    dimensions: "6.2\" H x 4.0\" W x 4.0\" D",
    weight: "3 lbs",
    material: "ABS & PVC",
    category: "Guardians of the Galaxy",
    availability: "Pre-Order",
    images: [
      "/assets/PRODUCTS/racoon/images/1.png",
      "/assets/PRODUCTS/racoon/images/2.png",
      "/assets/PRODUCTS/racoon/images/3.png"
    ]
  },
  {
    id: 4,
    name: "Spider-Man vs Venom",
    subtitle: "Symbiote Feud",
    description: "Recreate the classic comic rivalry. Spider-Man swings clear of Venom's creeping symbiotic tendrils.",
    price: 820,
    scale: "1:4 Scale",
    dimensions: "26.5\" H x 15.0\" W x 14.0\" D",
    weight: "22 lbs",
    material: "Polystone",
    category: "Spider-Man",
    availability: "Available Now",
    images: [
      "/assets/PRODUCTS/spider venom/images/1.png",
      "/assets/PRODUCTS/spider venom/images/2.png",
      "/assets/PRODUCTS/spider venom/images/3.png",
      "/assets/PRODUCTS/spider venom/images/4.png"
    ]
  },
  {
    id: 5,
    name: "Cyclops",
    subtitle: "X-Men Leader",
    description: "Scott Summers in his classic blue and yellow uniform, captured in a commanding optic blast pose.",
    price: 350,
    scale: "1:4 Scale",
    dimensions: "14.0\" H x 9.0\" W x 9.5\" D",
    weight: "10 lbs",
    material: "Polystone",
    category: "X-Men",
    availability: "Available Now",
    images: [
      "/work-images/WhatsApp Image 2025-09-08 at 23.09.20_61c14db9.jpg",
      "/work-images/WhatsApp Image 2025-09-08 at 23.09.20_9e57342c.jpg",
      "/work-images/WhatsApp Image 2025-09-08 at 23.09.20_e7c1121a.jpg"
    ]
  },
  {
    id: 6,
    name: "Star-Lord",
    subtitle: "Peter Quill",
    description: "The charismatic leader of the Guardians in his trench coat and helmet, with a baby Groot on his shoulder.",
    price: 295,
    scale: "1:6 Scale",
    dimensions: "12.2\" H x 6.0\" W x 5.0\" D",
    weight: "5 lbs",
    material: "Fabric, PVC & ABS",
    category: "Guardians of the Galaxy",
    availability: "Pre-Order",
    images: [
      "/work-images/WhatsApp Image 2025-09-08 at 23.10.49_6c3b4e98.jpg",
      "/work-images/WhatsApp Image 2025-09-08 at 23.10.49_85faaf8c.jpg",
      "/work-images/WhatsApp Image 2025-09-08 at 23.10.49_a542d916.jpg"
    ]
  },
  {
    id: 7,
    name: "Groot",
    subtitle: "Flora Colossus",
    description: "Groot in his battle stance, extending branches. Complete with detailed bark texturing and purple accent base flowers.",
    price: 620,
    scale: "1:4 Scale",
    dimensions: "24.0\" H x 14.0\" W x 13.0\" D",
    weight: "20 lbs",
    material: "Polystone",
    category: "Guardians of the Galaxy",
    availability: "Available Now",
    images: [
      "/work-images/groot-1.jpg",
      "/work-images/groot-2.jpg",
      "/work-images/groot-3.jpg"
    ]
  },
  {
    id: 8,
    name: "Thanos",
    subtitle: "The Mad Titan",
    description: "Thanos stands victorious in full golden armor, wielding his massive double-bladed sword.",
    price: 950,
    scale: "1:4 Scale",
    dimensions: "30.5\" H x 20.0\" W x 18.0\" D",
    weight: "35 lbs",
    material: "Polystone",
    category: "Avengers",
    availability: "Available Now",
    images: [
      "/work-images/thanos-2.jpg",
      "/work-images/thanos-1.jpg"
    ]
  },
  {
    id: 9,
    name: "Drax the Destroyer",
    subtitle: "Guardians of the Galaxy",
    description: "The combat-hardened Drax with detailed body markings, wielding dual daggers and a heavy shoulder-cannon.",
    price: 340,
    scale: "1:6 Scale",
    dimensions: "12.8\" H x 7.0\" W x 6.0\" D",
    weight: "7 lbs",
    material: "Polystone",
    category: "Guardians of the Galaxy",
    availability: "Pre-Order",
    images: [
      "/work-images/IMG_1694.JPEG.jpg"
    ]
  },
  {
    id: 10,
    name: "Black Panther",
    subtitle: "King of Wakanda",
    description: "Poised in a dynamic mid-air leap off a Wakandan rocky ledge with metallic structures and scratch marks.",
    price: 680,
    scale: "1:4 Scale",
    dimensions: "25.0\" H x 13.0\" W x 12.0\" D",
    weight: "16 lbs",
    material: "Polystone",
    category: "Avengers",
    availability: "Available Now",
    images: [
      "/work-images/IMG_1701.JPEG.jpg"
    ]
  }
];

interface GalleryProps {
  searchTerm: string;
  cartItems: CartItem[];
  isCartOpen: boolean;
  onCloseCart: () => void;
  onAddToCart: (product: any) => void;
  onUpdateCartQuantity: (id: number, quantity: number) => void;
  onRemoveFromCart: (id: number) => void;
  onOpenCart: () => void;
  isFeaturedOnly?: boolean;
  setViewShopPage?: () => void;
}

const Gallery: React.FC<GalleryProps> = ({
  searchTerm,
  cartItems,
  isCartOpen,
  onCloseCart,
  onAddToCart,
  onUpdateCartQuantity,
  onRemoveFromCart,
  onOpenCart,
  isFeaturedOnly = false,
  setViewShopPage
}) => {
  // Filter States
  const [selectedAvailabilities, setSelectedAvailabilities] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [localSearch, setLocalSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('default');

  // Quick View Modal States
  const [selectedProduct, setSelectedProduct] = useState<typeof products[0] | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Card Image Hover states
  const [hoveredCardId, setHoveredCardId] = useState<number | null>(null);

  // Categories and types extracted dynamically or defined statically
  const availabilityOptions = ["Available Now", "Pre-Order", "Sold Out"];
  const categoryOptions = ["Avengers", "Spider-Man", "Guardians of the Galaxy", "X-Men"];
  const typeOptions = ["Sixth Scale Figure", "Quarter Scale Statue", "1:4 Scale Bust"];

  // Filter handlers
  const handleAvailabilityToggle = (avail: string) => {
    setSelectedAvailabilities(prev => 
      prev.includes(avail) ? prev.filter(a => a !== avail) : [...prev, avail]
    );
  };

  const handleCategoryToggle = (cat: string) => {
    setSelectedCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const handleTypeToggle = (type: string) => {
    setSelectedTypes(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const handleClearFilters = () => {
    setSelectedAvailabilities([]);
    setSelectedCategories([]);
    setSelectedTypes([]);
    setLocalSearch('');
  };

  // Filter & Sort Logic
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // Search term from top bar or local sidebar
      const matchGlobalSearch = searchTerm 
        ? product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.subtitle.toLowerCase().includes(searchTerm.toLowerCase())
        : true;
      const matchLocalSearch = localSearch
        ? product.name.toLowerCase().includes(localSearch.toLowerCase()) || product.subtitle.toLowerCase().includes(localSearch.toLowerCase())
        : true;
        
      // Availability filter
      const matchAvail = selectedAvailabilities.length > 0 
        ? selectedAvailabilities.includes(product.availability)
        : true;
        
      // Category filter
      const matchCat = selectedCategories.length > 0
        ? selectedCategories.includes(product.category)
        : true;
        
      // Product Type filter (Scale matching)
      const matchType = selectedTypes.length > 0
        ? selectedTypes.some(type => product.scale.includes(type.split(" ")[0]) || (type.includes("Bust") && product.scale.includes("Bust")))
        : true;

      return matchGlobalSearch && matchLocalSearch && matchAvail && matchCat && matchType;
    }).sort((a, b) => {
      if (sortOrder === 'price-low') return a.price - b.price;
      if (sortOrder === 'price-high') return b.price - a.price;
      if (sortOrder === 'name-asc') return a.name.localeCompare(b.name);
      return 0; // default (id)
    });
  }, [searchTerm, localSearch, selectedAvailabilities, selectedCategories, selectedTypes, sortOrder]);

  const cartTotal = cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);

  const renderProductGrid = (productList: typeof products, isGridFeaturedOnly = false) => {
    return (
      <ProductGrid isFeaturedOnly={isGridFeaturedOnly}>
        {productList.map((product) => {
          const hasAlternateImages = product.images.length > 1;
          const isHovered = hoveredCardId === product.id;

          return (
            <ProductCard 
              key={product.id}
              onMouseEnter={() => setHoveredCardId(product.id)}
              onMouseLeave={() => setHoveredCardId(null)}
            >
              <Badge availability={product.availability}>{product.availability}</Badge>
              
              <CardImageWrapper onClick={() => {
                setSelectedProduct(product);
                setActiveImageIndex(0);
              }}>
                <CardImage 
                  src={product.images[0]} 
                  alt={product.name} 
                  isHovered={isHovered}
                />
                {hasAlternateImages && (
                  <CardImageAlt 
                    src={product.images[1]} 
                    alt={`${product.name} alternate view`} 
                    isVisible={isHovered}
                  />
                )}
                <HoverOverlay isVisible={isHovered}>
                  <QuickViewBtn>Quick View</QuickViewBtn>
                </HoverOverlay>
              </CardImageWrapper>

              <CardInfo>
                <BrandLabel>KIII TOYS STUDIOS</BrandLabel>
                <ProductTitle>{product.name}</ProductTitle>
                <ProductSubtitle>{product.subtitle}</ProductSubtitle>
                
                <CardBottom>
                  <PriceTag>${product.price}</PriceTag>
                  <AddCartIconBtn 
                    onClick={() => onAddToCart({
                      id: product.id,
                      name: product.name,
                      price: product.price,
                      scale: product.scale,
                      image: product.images[0]
                    })}
                    disabled={product.availability === 'Sold Out'}
                    title={product.availability === 'Sold Out' ? 'Out of stock' : 'Add to Cart'}
                  >
                    {React.createElement(MdShoppingCart as React.ComponentType<any>)}
                  </AddCartIconBtn>
                </CardBottom>
              </CardInfo>
            </ProductCard>
          );
        })}
      </ProductGrid>
    );
  };

  return (

    <CatalogLayout id="gallery" isFeaturedOnly={isFeaturedOnly}>
      {/* Sidebar Filters */}
      {!isFeaturedOnly && (
        <FilterSidebar>
          <FilterSectionTitle>
            Filters
            {React.createElement(MdRefresh as React.ComponentType<any>, {
              style: { cursor: 'pointer', fontSize: '1.25rem' },
              onClick: handleClearFilters,
              title: "Reset Filters"
            })}
          </FilterSectionTitle>
          
          <FilterGroup>
            <FilterLabel>Search Name</FilterLabel>
            <SearchBox>
              {React.createElement(MdSearch as React.ComponentType<any>, {
                style: { color: 'var(--color-slate-500)' }
              })}
              <SearchInput 
                type="text" 
                placeholder="Search figure..." 
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
              />
            </SearchBox>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Availability</FilterLabel>
            {availabilityOptions.map(avail => (
              <CheckboxContainer key={avail}>
                <input 
                  type="checkbox" 
                  checked={selectedAvailabilities.includes(avail)}
                  onChange={() => handleAvailabilityToggle(avail)}
                />
                {avail}
              </CheckboxContainer>
            ))}
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Franchise / License</FilterLabel>
            {categoryOptions.map(cat => (
              <CheckboxContainer key={cat}>
                <input 
                  type="checkbox" 
                  checked={selectedCategories.includes(cat)}
                  onChange={() => handleCategoryToggle(cat)}
                />
                {cat}
              </CheckboxContainer>
            ))}
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>Scale / Format</FilterLabel>
            {typeOptions.map(type => (
              <CheckboxContainer key={type}>
                <input 
                  type="checkbox" 
                  checked={selectedTypes.includes(type)}
                  onChange={() => handleTypeToggle(type)}
                />
                {type}
              </CheckboxContainer>
            ))}
          </FilterGroup>
          
          <ClearButton onClick={handleClearFilters}>
            Clear All Filters
          </ClearButton>
        </FilterSidebar>
      )}

      {/* Main Catalog Content */}
      <CatalogContent style={isFeaturedOnly ? { width: '100%', maxWidth: '1200px', margin: '0 auto' } : {}}>
        {isFeaturedOnly ? (
          (() => {
            const availableNow = filteredProducts.filter(p => p.availability === 'Available Now');
            const preOrder = filteredProducts.filter(p => p.availability === 'Pre-Order');

            return (
              <>
                {/* Section 1: Featured Collectibles */}
                <FeaturedHeader>
                  <FeaturedSub>Curated Masterpieces</FeaturedSub>
                  <FeaturedTitle>Featured Collectibles</FeaturedTitle>
                </FeaturedHeader>
                {renderProductGrid(availableNow.slice(0, 4), true)}
                
                {availableNow.length > 4 && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2.5rem', marginBottom: '1.5rem' }}>
                    <ViewAllButton onClick={() => {
                      if (setViewShopPage) {
                        setViewShopPage();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}>
                      View All Collectibles
                    </ViewAllButton>
                  </div>
                )}

                {/* Section 2: Pre-Order */}
                <SectionDivider className="luxury-section-divider" />
                <FeaturedHeader style={{ marginTop: '5rem' }}>
                  <FeaturedSub>Reserve Yours Today</FeaturedSub>
                  <FeaturedTitle>Pre-Order Collectibles</FeaturedTitle>
                </FeaturedHeader>
                {renderProductGrid(preOrder.slice(0, 4), true)}

                {preOrder.length > 4 && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2.5rem', marginBottom: '1.5rem' }}>
                    <ViewAllButton onClick={() => {
                      if (setViewShopPage) {
                        setViewShopPage();
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}>
                      View All Pre-Orders
                    </ViewAllButton>
                  </div>
                )}
              </>
            );
          })()
        ) : (
          <>
            <CatalogHeader>
              <ResultsCount>
                Showing <strong>{filteredProducts.length}</strong> of <strong>{products.length}</strong> Collectibles
              </ResultsCount>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--color-slate-400)' }}>Sort By:</span>
                <SortDropdown value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                  <option value="default">Featured</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="name-asc">Alphabetical</option>
                </SortDropdown>
              </div>
            </CatalogHeader>
            {renderProductGrid(filteredProducts, false)}

            {filteredProducts.length === 0 && (
              <div style={{ textAlign: 'center', padding: '5rem 0', color: 'var(--color-slate-400)' }}>
                <h3>No collectibles matched your filter criteria.</h3>
                <p style={{ marginTop: '0.5rem' }}>Try clearing some filters or refining your search name.</p>
                <ClearButton style={{ maxWidth: '200px', margin: '2rem auto 0' }} onClick={handleClearFilters}>
                  Reset Filters
                </ClearButton>
              </div>
            )}
          </>
        )}


        {isFeaturedOnly && (
          <BrandCollabWrapper>
            <BrandCollabHeading>Collaborations</BrandCollabHeading>
            <BrandPartnersBar>
              <BrandPartnerLogo src="/assets/titles/1.png" alt="Ramayan" />
              <BrandPartnerLogo 
                src="/assets/titles/2.png" 
                alt="Salaar" 
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const el = document.getElementById('upcoming');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
              />
              <BrandPartnerLogo src="/assets/titles/3.png" alt="Kalki" />
              <BrandPartnerLogo src="/assets/titles/4.png" alt="Eternal War" />
              <BrandPartnerLogo src="/assets/titles/5.png" alt="Bahubali" />
            </BrandPartnersBar>
          </BrandCollabWrapper>
        )}
      </CatalogContent>

      {/* Quick View Modal */}
      {selectedProduct && (
        <ModalOverlay onClick={() => setSelectedProduct(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalCloseBtn onClick={() => setSelectedProduct(null)} aria-label="Close modal">
              {React.createElement(MdClose as React.ComponentType<any>)}
            </ModalCloseBtn>
            
            <ModalGallery>
              <MainImage src={selectedProduct.images[activeImageIndex]} alt={selectedProduct.name} />
              {selectedProduct.images.length > 1 && (
                <ThumbnailContainer>
                  {selectedProduct.images.map((img, idx) => (
                    <Thumbnail 
                      key={idx} 
                      src={img} 
                      alt="" 
                      isActive={activeImageIndex === idx} 
                      onClick={() => setActiveImageIndex(idx)}
                    />
                  ))}
                </ThumbnailContainer>
              )}
            </ModalGallery>

            <ModalDetails>
              <BrandLabel>KIII Toys Studios • {selectedProduct.category}</BrandLabel>
              <ModalTitle>{selectedProduct.name}</ModalTitle>
              <ModalPrice>${selectedProduct.price}</ModalPrice>
              <ModalDesc>{selectedProduct.description}</ModalDesc>
              
              <SpecsGrid>
                <SpecItem>
                  <span className="label">Format</span>
                  <span className="value">{selectedProduct.scale}</span>
                </SpecItem>
                <SpecItem>
                  <span className="label">Materials</span>
                  <span className="value">{selectedProduct.material}</span>
                </SpecItem>
                <SpecItem>
                  <span className="label">Dimensions</span>
                  <span className="value">{selectedProduct.dimensions}</span>
                </SpecItem>
                <SpecItem>
                  <span className="label">Weight</span>
                  <span className="value">{selectedProduct.weight}</span>
                </SpecItem>
              </SpecsGrid>

              <ModalActions>
                <AddToCartBtn 
                  onClick={() => {
                    onAddToCart({
                      id: selectedProduct.id,
                      name: selectedProduct.name,
                      price: selectedProduct.price,
                      scale: selectedProduct.scale,
                      image: selectedProduct.images[0]
                    });
                    setSelectedProduct(null);
                  }}
                  disabled={selectedProduct.availability === 'Sold Out'}
                >
                  {selectedProduct.availability === 'Sold Out' ? 'Sold Out' : `Add to Cart - $${selectedProduct.price}`}
                </AddToCartBtn>
              </ModalActions>
            </ModalDetails>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Cart Drawer */}
      <CartDrawerOverlay isOpen={isCartOpen} onClick={onCloseCart} />
      <CartDrawer isOpen={isCartOpen}>
        <CartHeader>
          <h3>Shopping Cart <span>({cartItems.reduce((a, item) => a + item.quantity, 0)})</span></h3>
          <CloseDrawerBtn onClick={onCloseCart} aria-label="Close cart">
            {React.createElement(MdClose as React.ComponentType<any>)}
          </CloseDrawerBtn>
        </CartHeader>

        <CartItemsContainer>
          {cartItems.length === 0 ? (
            <EmptyCartState>
              {React.createElement(MdShoppingCart as React.ComponentType<any>, { className: "cart-icon" })}
              <h4>Your Cart is Empty</h4>
              <p style={{ fontSize: '0.85rem' }}>Add some premium collectibles to start your legendary display.</p>
              <CheckoutBtn style={{ maxWidth: '200px', marginTop: '1rem', padding: '0.75rem 1rem' }} onClick={onCloseCart}>
                Browse Catalog
              </CheckoutBtn>
            </EmptyCartState>
          ) : (
            cartItems.map((item) => (
              <CartItemRow key={item.id}>
                <CartItemThumb src={item.image} alt={item.name} />
                <CartItemDetails>
                  <h4>{item.name}</h4>
                  <p className="scale">{item.scale}</p>
                  <span className="price">${item.price}</span>
                </CartItemDetails>
                
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                  <DeleteBtn onClick={() => onRemoveFromCart(item.id)} aria-label="Remove item">
                    {React.createElement(MdDelete as React.ComponentType<any>)}
                  </DeleteBtn>
                  
                  <QtyControls>
                    <button onClick={() => onUpdateCartQuantity(item.id, item.quantity - 1)}>-</button>
                    <span>{item.quantity}</span>
                    <button onClick={() => onUpdateCartQuantity(item.id, item.quantity + 1)}>+</button>
                  </QtyControls>
                </div>
              </CartItemRow>
            ))
          )}
        </CartItemsContainer>

        {cartItems.length > 0 && (
          <CartFooter>
            <CartSummaryRow>
              <span className="label">Subtotal</span>
              <span className="total">${cartTotal.toLocaleString()}</span>
            </CartSummaryRow>
            <CheckoutBtn onClick={() => {
              alert('Checkout system integration coming soon! Thank you for supporting KIII Toys Studios.');
              onCloseCart();
            }}>
              Proceed to Secure Checkout
            </CheckoutBtn>
          </CartFooter>
        )}
      </CartDrawer>
    </CatalogLayout>
  );
};

export default Gallery;